import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Checkbox, FormControlLabel,
   FormGroup, Button, Card, Grid, Divider } from '@mui/material';
import OrganizationsHeader from '../DownloadProject';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';

const ProjectForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    projectDuration: '',
    weeklyTimeCommitment: '',
    modeOfWork: '',
    desiredStartDate: '',
    applicationDeadline: '',
    compensation: '',
    compensationType: '',
    confidentialityOrIPClauses: '',
    pointOfContactName: '',
    pointOfContactDesignation: '',
    pointOfContactEmail: '',
    pointOfContactPhoneNumber: '',
  });

  const [formErrors, setFormErrors] = useState({});

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));

    if (formErrors[field]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = {};

    if (!form.projectDuration) errors.projectDuration = 'Required';
    if (!form.modeOfWork) errors.modeOfWork = 'Select a mode';
    if (!form.pointOfContactName) errors.pointOfContactName = 'Required';
    if (!form.pointOfContactDesignation) errors.pointOfContactDesignation = 'Required';

    if (!form.pointOfContactEmail) {
      errors.pointOfContactEmail = 'Required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.pointOfContactEmail)) {
      errors.pointOfContactEmail = 'Invalid email';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    localStorage.setItem('projectFormData', JSON.stringify(form));
    navigate('/organizations/project-submission');
  };

  useEffect(() => {
    const stored = localStorage.getItem('projectFormData');
    if (stored) {
      setForm(JSON.parse(stored));
    }
  }, []);

  useEffect(() => {
    if (Object.keys(formErrors).length > 0) {
      const firstErrorKey = Object.keys(formErrors)[0];
      const element = document.querySelector(`[name="${firstErrorKey}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [formErrors]);

  return (
    <div className="students-flex">
      <OrganizationsHeader />
       <Box
        sx={{
            background: 'linear-gradient(to right, #e0f7fa, #f1f8e9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            height : '91vh',
            overflowY: 'auto',
            padding: "40px"
        }}
        >
        <Card sx={{ width : '100%', maxWidth : 900, p: 4, boxShadow: 6, borderRadius: 4 }}>
          <Box sx={{ px: { xs: 2, sm: 1 }, py: 1 }}>
           <Typography
            variant="h4"
            fontWeight={700}
            sx={{ fontSize: { xs: '1rem', sm: '1.4rem' } }}
            gutterBottom
            textAlign="center"
          >
            Submit Your Project
          </Typography>
            <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            mb={4}
            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
            Provide detailed information about your company project.
          </Typography>

            {/* Timeline & Engagement */}
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mt: 4 }}
                >
                  Section 3: Project Duration & Work Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="projectDuration"
                      fullWidth
                      label="10. Project Duration (in weeks)"
                      value={form.projectDuration}
                      onChange={handleChange('projectDuration')}
                      placeholder="Enter expected duration in weeks"
                      InputLabelProps={{
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                    }}
                    inputProps={{
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                    }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="weeklyTimeCommitment"
                      fullWidth
                      label="11. Weekly Time Commitment (hrs)"
                      value={form.weeklyTimeCommitment}
                      onChange={handleChange('weeklyTimeCommitment')}
                      error={!!formErrors.weeklyTimeCommitment}
                      helperText={formErrors.weeklyTimeCommitment}
                      placeholder="Enter estimated hours per week"
                      InputLabelProps={{
                        sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                      }}
                      inputProps={{
                        sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                      }}
                    />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      sx={{ fontSize: { xs: '0.95rem', sm: '1rem' }, mt: 2 }}
                    >
                      12. Mode of Work
                    </Typography>
                    <Box
                      border={`1px solid ${formErrors.modeOfWork ? 'red' : 'transparent'}`}
                      borderRadius={1}
                      p={1}
                    >
                      <FormGroup row>
                        {['Remote', 'Onsite', 'Hybrid'].map((mode) => (
                          <FormControlLabel
                            key={mode}
                            control={
                              <Checkbox
                                checked={form.modeOfWork === mode}
                                onChange={() =>
                                  setForm((prev) => ({ ...prev, modeOfWork: mode }))
                                }
                              />
                            }
                            label={mode}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                    {formErrors.modeOfWork && (
                      <Typography color="error" variant="caption">
                        {formErrors.modeOfWork}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="desiredStartDate"
                      fullWidth
                      type="date"
                      label="13. Desired Start Date"
                      value={form.desiredStartDate}
                      onChange={handleChange('desiredStartDate')}
                      InputLabelProps={{
                      shrink: true,
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                      }}
                      inputProps={{
                        sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="applicationDeadline"
                      fullWidth
                      type="date"
                      label="14. Project Deadline"
                      value={form.applicationDeadline}
                      onChange={handleChange('applicationDeadline')}
                      InputLabelProps={{
                      shrink: true,
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                    }}
                    inputProps={{
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                    }}
                    />
                  </Grid>
                </Grid>

            {/* Compensation & IP */}
               <Typography
                variant="h6"
                fontWeight={600}
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mt: 4 }}
                >
                  Section 4: Compensation & IP
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                   <Typography
                    variant="subtitle1"
                    fontWeight={500}
                    sx={{ fontSize: { xs: '0.95rem', sm: '1rem' }, mt: 2 }}
                    >
                    15. Compensation (if applicable)
                    </Typography>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.compensationType === 'Paid'}
                            onChange={() =>
                              setForm((prev) => ({
                                ...prev,
                                compensationType: 'Paid',
                              }))
                            }
                          />
                        }
                        label="Paid"
                      />
                      {form.compensationType === 'Paid' && (
                        <TextField
                          label="Amount (₹)"
                          type="number"
                          size="small"
                          value={form.compensation}
                          onChange={handleChange('compensation')}
                          sx={{ ml: 2 }}
                        />
                      )}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.compensationType === 'Unpaid'}
                            onChange={() =>
                              setForm((prev) => ({
                                ...prev,
                                compensationType: 'Unpaid',
                                compensation: '',
                              }))
                            }
                          />
                        }
                        label="Unpaid / Voluntary"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.compensationType === 'Discuss'}
                            onChange={() =>
                              setForm((prev) => ({
                                ...prev,
                                compensationType: 'Discuss',
                                compensation: '',
                              }))
                            }
                          />
                        }
                        label="To Be Discussed"
                      />
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="16. Confidentiality / IP Clauses (optional)"
                      multiline
                      rows={3}
                      value={form.confidentialityOrIPClauses}
                      onChange={handleChange('confidentialityOrIPClauses')}
                      InputLabelProps={{
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                      }}
                      inputProps={{
                        sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                      }}
                    />
                  </Grid>
                </Grid>
            {/* Point of Contact */}
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mt: 4 }}
              >
                Section 5: Point of Contact
              </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="pointOfContactName"
                      fullWidth
                      label="Name"
                      value={form.pointOfContactName}
                      onChange={handleChange('pointOfContactName')}
                      error={!!formErrors.pointOfContactName}
                      helperText={formErrors.pointOfContactName}
                      InputLabelProps={{
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                    }}
                    inputProps={{
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                    }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="pointOfContactDesignation"
                      fullWidth
                      label="Designation"
                      value={form.pointOfContactDesignation}
                      onChange={handleChange('pointOfContactDesignation')}
                      error={!!formErrors.pointOfContactDesignation}
                      helperText={formErrors.pointOfContactDesignation}
                      InputLabelProps={{
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                      }}
                      inputProps={{
                        sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="pointOfContactEmail"
                      fullWidth
                      label="Email ID"
                      type="pointOfContactEmail"
                      value={form.pointOfContactEmail}
                      onChange={handleChange('pointOfContactEmail')}
                      error={!!formErrors.pointOfContactEmail}
                      helperText={formErrors.pointOfContactEmail}
                      InputLabelProps={{
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                      }}
                      inputProps={{
                        sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PhoneInput
                      country={'in'}
                      value={form.pointOfContactPhoneNumber}
                      inputStyle={{ inlineSize: '100%' }}
                      onChange={(phone) =>
                        setForm((prev) => ({ ...prev, pointOfContactPhoneNumber: phone }))
                      }
                      specialLabel="Phone Number"
                      inputProps={{ name: 'pointOfContactPhoneNumber'}}
                    />
                    {formErrors.pointOfContactPhoneNumber && (
                      <Typography color="error" variant="caption">
                        {formErrors.pointOfContactPhoneNumber}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

            {/* Buttons */}
            <Box textAlign="center" sx={{ mb: 3, paddingTop : 6 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/organizations/new-post-project')}
                sx={{ px: 4, py: 1,  fontSize: { xs: '0.85rem', sm: '1rem' }, }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                sx={{ px: 5, py: 1.5, fontWeight: 500, ml: 3,  fontSize: { xs: '0.85rem', sm: '1rem' }, }}
              >
                Save and Continue
              </Button>
            </Box>
          </Box>
        </Card>
      </Box>
    </div>
  );
};

export default ProjectForm;
